import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { routes, scheduledJobOutcomeSchema, type ScheduledJobOutcome } from "@nova-ai/shared"
import { z } from "zod"

import { apiFetch } from "../lib/api"
import { queryKeys } from "./query-keys"

export type ScheduleSlot = {
  instant: string
  localSlot: string
}

export type ScheduledJob = {
  id: string
  purpose: string
  handler: { key: string; version: number }
  schedule: { cron: string; timeZone: string; version: number }
  enabled: boolean
  manualOnly: boolean
  recentRuns: { status: string; triggeredAt: string; durationMs: number | null }[]
  stats: { finished: number; succeeded: number; avgDurationMs: number | null }
  parameters: { version: 1 }
  nextRunAt: string | null
  nextSlots: ScheduleSlot[]
  runSummary: ScheduledJobRunSummary
  updatedAt: string
}

export type ScheduledJobRun = {
  id: string
  scheduledJobId: string
  trigger: string
  authenticatedExecutor: string | null
  triggeredBy: string | null
  scheduleVersion: number
  localSlot: string | null
  scheduledFor: string | null
  triggeredAt: string
  status: string
  effectiveStatus: string
  startedAt: string | null
  finishedAt: string | null
  updatedAt: string
  coalescedIntoRunId: string | null
  predecessorRunId: string | null
  successorRunId: string | null
  continuationReason: string | null
  workload: {
    id: string
    status: string
    updatedAt: string
    finishedAt: string | null
    product: ProductSyncJobReadBack | null
  } | null
  outcome: ScheduledJobOutcome | null
  failureSummary: string | null
}

type ProductSyncJobReadBack = {
  progress: { storesDone: number; storesTotal: number; currentStore: string | null }
  result: { succeeded: number; failed: number; totalElapsedMs: number } | null
}

export type ScheduledJobRunSummary = {
  current: ScheduledJobRun | null
  last: ScheduledJobRun | null
  latestFailure: ScheduledJobRun | null
}

export type ScheduleRevisionState = {
  cronExpression: string
  timeZone: string
  enabled: boolean
  parameters: { version: 1 }
  nextRunAt: string | null
}

export type ScheduleRevision = {
  version: number
  before: ScheduleRevisionState
  after: ScheduleRevisionState
  authenticatedExecutor: string
  attributedHuman: string
  source: "manual_ui" | "manual_cli"
  createdAt: string
}

type SchedulePreview = {
  schedule: { cron: string; timeZone: string }
  nextSlots: ScheduleSlot[]
}

type ScheduleUpdateInput = {
  id: string
  expectedVersion: number
  cronExpression?: string
  timeZone?: string
  enabled?: boolean
}

const scheduledJobRunSchema = z.object({
  id: z.string(),
  scheduledJobId: z.string(),
  trigger: z.string(),
  authenticatedExecutor: z.string().nullable(),
  triggeredBy: z.string().nullable(),
  scheduleVersion: z.number(),
  localSlot: z.string().nullable(),
  scheduledFor: z.string().nullable(),
  triggeredAt: z.string(),
  status: z.string(),
  effectiveStatus: z.string(),
  startedAt: z.string().nullable(),
  finishedAt: z.string().nullable(),
  updatedAt: z.string(),
  coalescedIntoRunId: z.string().nullable(),
  predecessorRunId: z.string().nullable(),
  successorRunId: z.string().nullable(),
  continuationReason: z.string().nullable(),
  workload: z
    .object({
      id: z.string(),
      status: z.string(),
      updatedAt: z.string(),
      finishedAt: z.string().nullable(),
      product: z
        .object({
          progress: z.object({
            storesDone: z.number(),
            storesTotal: z.number(),
            currentStore: z.string().nullable(),
          }),
          result: z
            .object({
              succeeded: z.number(),
              failed: z.number(),
              totalElapsedMs: z.number(),
            })
            .nullable(),
        })
        .nullable()
        .default(null),
    })
    .nullable(),
  outcome: scheduledJobOutcomeSchema.nullable().default(null),
  failureSummary: z.string().nullable(),
})

const scheduledJobSchema = z.object({
  id: z.string(),
  purpose: z.string(),
  handler: z.object({ key: z.string(), version: z.number() }),
  schedule: z.object({ cron: z.string(), timeZone: z.string(), version: z.number() }),
  enabled: z.boolean(),
  manualOnly: z.boolean(),
  recentRuns: z
    .array(
      z.object({ status: z.string(), triggeredAt: z.string(), durationMs: z.number().nullable() })
    )
    .max(20),
  stats: z.object({
    finished: z.number(),
    succeeded: z.number(),
    avgDurationMs: z.number().nullable(),
  }),
  parameters: z.object({ version: z.literal(1) }),
  nextRunAt: z.string().nullable(),
  nextSlots: z.array(z.object({ instant: z.string(), localSlot: z.string() })),
  runSummary: z.object({
    current: scheduledJobRunSchema.nullable(),
    last: scheduledJobRunSchema.nullable(),
    latestFailure: scheduledJobRunSchema.nullable(),
  }),
  updatedAt: z.string(),
})

const conflictDetailsSchema = z.object({ current: scheduledJobSchema })

export class ScheduleConflictError extends Error {
  constructor(
    message: string,
    readonly current: ScheduledJob
  ) {
    super(message)
    this.name = "ScheduleConflictError"
  }
}

export function useScheduledJobs() {
  return useQuery({
    queryKey: queryKeys.scheduledJobs,
    queryFn: async () => {
      const result = await apiFetch<{ jobs: ScheduledJob[] }>(routes.scheduledJobs.list())
      if (!result.ok) {
        throw new Error(result.error.message)
      }
      return result.value.jobs.map((job) => scheduledJobSchema.parse(job))
    },
    refetchInterval: 10_000,
  })
}

export function useScheduledJobRevisions(id: string | null) {
  return useQuery({
    queryKey: queryKeys.scheduledJobRevisions(id ?? "none"),
    queryFn: async () => {
      if (!id) {
        return []
      }
      const result = await apiFetch<{ revisions: ScheduleRevision[] }>(
        routes.scheduledJobs.revisions({ id }),
        { query: { limit: 25 } }
      )
      if (!result.ok) {
        throw new Error(result.error.message)
      }
      return result.value.revisions
    },
    enabled: id !== null,
  })
}

const ACTIVE_RUN_STATUSES = new Set(["queued", "running"])
const ACTIVE_RUN_REFETCH_INTERVAL_MS = 5_000
const IDLE_RUN_REFETCH_INTERVAL_MS = 10_000

export function isScheduledJobRunActive(run: ScheduledJobRun): boolean {
  return ACTIVE_RUN_STATUSES.has(run.effectiveStatus)
}

export function scheduledJobRunsRefetchInterval(
  runs: readonly ScheduledJobRun[] | undefined
): number {
  return runs?.some(isScheduledJobRunActive)
    ? ACTIVE_RUN_REFETCH_INTERVAL_MS
    : IDLE_RUN_REFETCH_INTERVAL_MS
}

export function useScheduledJobRuns(id: string | null) {
  return useQuery({
    queryKey: queryKeys.scheduledJobRuns(id ?? "none"),
    queryFn: async () => {
      if (!id) {
        return []
      }
      const result = await apiFetch<{ runs: ScheduledJobRun[] }>(
        routes.scheduledJobs.runs({ id }),
        { query: { limit: 25 } }
      )
      if (!result.ok) {
        throw new Error(result.error.message)
      }
      return result.value.runs.map((run) => scheduledJobRunSchema.parse(run))
    },
    enabled: id !== null,
    refetchInterval: (query) => scheduledJobRunsRefetchInterval(query.state.data),
  })
}

export function useScheduledJobPreview(
  input: { id: string; cronExpression: string; timeZone: string },
  enabled: boolean
) {
  return useQuery({
    queryKey: queryKeys.scheduledJobPreview(input.id, input.cronExpression, input.timeZone),
    enabled,
    retry: false,
    queryFn: async ({ signal }) => {
      const result = await apiFetch<SchedulePreview>(
        routes.scheduledJobs.preview({ id: input.id }),
        {
          method: "POST",
          signal,
          body: { cronExpression: input.cronExpression, timeZone: input.timeZone, count: 6 },
        }
      )
      if (!result.ok) {
        throw new Error(result.error.message)
      }
      return result.value
    },
  })
}

export function useUpdateScheduledJob() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: ScheduleUpdateInput) => {
      const result = await apiFetch<{
        previous: ScheduleRevisionState
        current: ScheduledJob
        revision: ScheduleRevision
      }>(routes.scheduledJobs.update({ id: input.id }), {
        method: "PUT",
        body: {
          expectedVersion: input.expectedVersion,
          cronExpression: input.cronExpression,
          timeZone: input.timeZone,
          enabled: input.enabled,
        },
      })
      if (!result.ok) {
        const conflict = conflictDetailsSchema.safeParse(result.error.details)
        if (result.error.kind === "conflict" && conflict.success) {
          throw new ScheduleConflictError(result.error.message, conflict.data.current)
        }
        throw new Error(result.error.message)
      }
      return result.value
    },
    onSettled: (_data, _error, input) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.scheduledJobs })
      void queryClient.invalidateQueries({ queryKey: queryKeys.scheduledJobRevisions(input.id) })
    },
  })
}

export function useRunScheduledJobNow() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const result = await apiFetch<{ run: ScheduledJobRun }>(routes.scheduledJobs.runNow({ id }), {
        method: "POST",
        body: {},
      })
      if (!result.ok) {
        throw new Error(result.error.message)
      }
      return result.value.run
    },
    onSuccess: (_data, id) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.scheduledJobs })
      void queryClient.invalidateQueries({ queryKey: queryKeys.scheduledJobRuns(id) })
    },
  })
}
